import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api.js';

/**
 * Documents step: detect a dropped document tree's file→patient layout and stage
 * a DocumentReference per confident match. The expand job records where each
 * zip/7z drop landed in S3, so the operator just clicks Detect — no prefix typing.
 * Matches → the common-schema cache (DocumentReference), copied into Cerbo
 * documents (S3 + pt_documents) on the next import; unmatched → the review queue.
 */
export function DocumentDetectPanel({ sessionId }: { sessionId: string }) {
  const qc = useQueryClient();
  const { data: srcData } = useQuery({
    queryKey: ['document-sources', sessionId],
    queryFn: () => api.getDocumentSources(sessionId),
    refetchInterval: 5000,
  });
  const sources = srcData?.sources ?? [];

  const { data: summary } = useQuery({
    queryKey: ['fhir-cache-summary', sessionId],
    queryFn: () => api.getFhirCacheSummary(sessionId),
    refetchInterval: 4000,
  });
  const docs = summary?.byType.find((t) => t.resourceType === 'DocumentReference');

  const detect = useMutation({
    mutationFn: () => api.detectDocuments(sessionId),
    onSuccess: (r) => {
      toast.success(`Document detection started (${r.jobIds.length} source${r.jobIds.length === 1 ? '' : 's'})`);
      void qc.invalidateQueries({ queryKey: ['fhir-cache-summary', sessionId] });
      void qc.invalidateQueries({ queryKey: ['fhir-cache', sessionId] });
    },
    onError: (e) => toast.error(`Detect failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  return (
    <div className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
      {sources.length === 0 ? (
        <div style={{ color: 'var(--text-dimmed)' }}>
          No document sources yet. Drop a zip/7z bundle and ingest it — the expand step records where its
          documents land, then this step matches them to patients.
        </div>
      ) : (
        <ul className="space-y-1">
          {sources.map((s, i) => (
            <li key={i} className="flex items-center gap-2 font-mono text-[12px]" style={{ color: 'var(--text-primary)' }}>
              <span style={{ color: 'var(--text-dimmed)' }}>📁</span>
              <span className="truncate" title={`${s.bucket}/${s.prefix}`}>{s.prefix}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => detect.mutate()}
          disabled={sources.length === 0 || detect.isPending}
          className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)' }}
          title="Match documents to patients (needs patients imported first) and stage a DocumentReference each"
        >
          {detect.isPending ? 'Detecting…' : '🔎 Detect & link documents'}
        </button>
        {docs && (
          <div className="text-[12px]">
            <span style={{ color: 'var(--accent-blue)' }}>{docs.mapped}</span> matched
            {docs.loaded > 0 && (
              <>
                {' · '}
                <span style={{ color: '#34d399' }}>{docs.loaded}</span> loaded
              </>
            )}
          </div>
        )}
      </div>

      <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>
        Run after patients are imported (matching needs the roster). Matches land in the common-schema
        cache as DocumentReference and copy into Cerbo documents on the next import; unmatched files go to
        the review queue.
      </div>
    </div>
  );
}
